import { theme } from '@/theme';
import { useSuspenseQuery } from '@apollo/client';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import NumericTextField from '@components/NumericTextField';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Button, InputAdornment, Skeleton, Typography } from '@mui/material';
import { Suspense, useEffect, useState, type FC } from 'react';
import { GREEN } from '../constants';
import { GET_GATE_LIMITS_DATA } from '../graphql/queries/getMoveGateModalData';
import { PositionSlider } from './Slider';

interface Props {
  gateId: string | null;
  setIsValid: (isValid: boolean) => void;
  position: string;
  setPosition: (value: string) => void;
  setInitialPosition: (value: number) => void;
}

const SuspenseComponent: FC<Omit<Props, 'gateId'> & { gateId: string }> = ({
  gateId,
  setIsValid,
  position,
  setPosition,
  setInitialPosition,
}) => {
  const { i18n } = useLingui();
  const { data } = useSuspenseQuery(GET_GATE_LIMITS_DATA, {
    fetchPolicy: 'no-cache',
    variables: { gateId },
  });
  const defaultPosition = data.gate.movements2.lastPosition?.[1] ?? 0;
  const isDirty = position !== String(defaultPosition);
  const { minPosition: min, maxPosition: max } = data.gate.dataConfiguration;
  const [error, setError] = useState('');

  useEffect(() => {
    const defaultPosition = data.gate.movements2.lastPosition?.[1] ?? 0;
    setPosition(String(defaultPosition));
    setInitialPosition(defaultPosition);
  }, [data, setPosition, setInitialPosition]);

  const onChangePosition = (value: string) => {
    setPosition(value);
  };

  const onResetPosition = () => {
    setPosition(String(defaultPosition));
  };

  useEffect(() => {
    const defaultPosition = data.gate.movements2.lastPosition?.[1] ?? 0;
    const { minPosition, maxPosition } = data.gate.dataConfiguration;
    const min = minPosition ?? 0;
    const max = maxPosition;

    const newPosition = position ? parseFloat(position) : null;

    if (!newPosition || newPosition === defaultPosition) {
      setIsValid(false);
      return;
    }

    if (newPosition < min) {
      setIsValid(false);
      setError(t(i18n)`La posición es menor al mínimo permitido`);
      return;
    }

    if (max && newPosition > max) {
      setIsValid(false);
      setError(t(i18n)`La posición es mayor al máximo permitido`);
      return;
    }

    setIsValid(true);
    setError('');
  }, [data, position, setIsValid, i18n]);

  const textColor = error ? theme.palette.error.dark : isDirty ? GREEN : 'white';

  return (
    <Box
      sx={{
        borderRadius: 1,
        bgcolor: '#171C24',
        px: '26px',
        py: 3,
        mt: 2,
      }}
    >
      <Typography fontWeight="medium" mb={2}>
        <Trans>Posición solicitada</Trans>
      </Typography>

      <NumericTextField
        error={Boolean(error)}
        helperText={error}
        value={String(position)}
        onChange={(event) => {
          const newPosition = event.target.value;
          onChangePosition(newPosition);
        }}
        fullWidth
        color="info"
        onBlur={() => {
          if (!position) onChangePosition(String(0));
        }}
        sx={{ width: '100%' }}
        inputProps={{
          style: {
            flexShrink: 0,
            // @ts-expect-error property available on future ts versions
            fieldSizing: 'content',
            maxWidth: 120,
            width: 'auto',
            letterSpacing: 1,
            color: textColor,
          },
        }}
        InputProps={{
          sx: {
            fontSize: 24,
            fontWeight: 'bold',
            px: 4,
          },
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                color={textColor}
                sx={{
                  fontSize: 24,
                  fontWeight: 'bold',
                }}
              >
                cm
              </Typography>
              {isDirty && (
                <Button color="info" onClick={onResetPosition}>
                  <Trans>Restablecer</Trans>
                </Button>
              )}
            </InputAdornment>
          ),
        }}
      />
      <PositionSlider
        error={error}
        defaultValue={defaultPosition}
        value={parseFloat(position)}
        onChange={(value) => onChangePosition(String(value))}
        min={min ?? 0}
        max={max ?? undefined}
      />
    </Box>
  );
};

export const MoveGateForm: FC<Props> = (props) => {
  if (!props.gateId) return null;

  const newProps = {
    ...props,
    gateId: props.gateId,
  };

  return (
    <ApolloErrorBoundary>
      <Suspense
        fallback={
          <>
            <Box
              sx={{
                borderRadius: 1,
                bgcolor: '#171C24',
                px: '26px',
                py: 3,
                mt: 2,
              }}
            >
              <Typography fontWeight="medium" mb={2}>
                <Trans>Posición solicitada</Trans>
              </Typography>

              <Skeleton variant="rounded" width="100%">
                <NumericTextField
                  fullWidth
                  defaultValue="300"
                  color="info"
                  InputProps={{ sx: { fontSize: 24, fontWeight: 'bold', px: 4 } }}
                />
              </Skeleton>
              <Skeleton variant="rounded" width="100%">
                <PositionSlider
                  defaultValue={0}
                  value={0}
                  onChange={() => {}}
                  min={0}
                  max={100}
                  error=""
                />
              </Skeleton>
            </Box>
          </>
        }
      >
        <SuspenseComponent {...newProps} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
